import {
  BookOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileSyncOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  SwapOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import Paper from 'components/@extended/Paper'
import ButtonActionBar from 'components/ButtonActionBar'
import EditUserDialog from 'components/EditUserDialog'
import MultiOrganizationSelector from 'components/MultiOrganizationSelector'
import RolloverPlanDialog from 'components/RolloverPlanDialog'
import SearchInput from 'components/SearchInput'
import SimpleTitle from 'components/SimpleTitle'
import SingleOrganizationSelector from 'components/SingleOrganizationSelector'
import SinglePeriodSelector from 'components/SinglePeriodSelector'
import UpdateCompensationDialog from 'components/UpdateCompensationDialog'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import useAuth from 'hooks/useAuth'
import { usePeriods } from 'hooks/usePeriods'
import {
  useArchivePlan,
  useBulkClosePlans,
  useClearFinalization,
  useClosePlan,
  useHidePlan,
  useReopenPlan,
  useShowPlan,
  useUpdatePlanOrganization,
} from 'hooks/usePlans'
import { usePlansMetadata, type PlanMetadata, type FinalizedFilter } from 'hooks/usePlansMetadata'
import { useSearchOrganizationTemplates } from 'hooks/useTemplates'
import type { FC } from 'react'
import { Suspense, startTransition, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { ClosePlanParams } from 'types/comp-plan'
import type { Period } from 'types/periods'
import {
  DATE_FORMAT,
  createMergedIdLabelMap,
  findPeriodById,
  formatDisplayDate,
  getCurrentRootPeriodId,
  parsePeriodDate,
} from 'types/periods'
import { Permissions } from 'types/permissions'
import type { UserProfile } from 'types/user-profile'
import { getOrganizationIdsWithPermissions, getRootOrganizationId } from 'types/user-profile'

dayjs.extend(utc)

const FILTER_STORAGE_KEY = 'periodPlansManagementFilters'

interface PersistedFilterState {
  periodId: string
  rootPeriodId: string
  search: string
  organizations: string[]
  template: string
  sort: string
  order: 'asc' | 'desc'
  finalizedFilter: FinalizedFilter
  includeRolledOver: boolean
  includeHidden: boolean
  includeVisible: boolean
  page: number
  pageSize: number
}

const PeriodPlansManagement: FC = () => {
  const { token, profile } = useAuth()

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <PeriodPlansManagementContent token={token!} profile={profile!} />
    </Suspense>
  )
}

function PeriodPlansManagementContent({ token, profile }: { token: string; profile: UserProfile }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const theme = useTheme()
  const navigate = useNavigate()

  // Check if URL has any filter params
  const hasUrlParams = useMemo(() => {
    const filterParams = [
      'periodId',
      'rootPeriodId',
      'search',
      'organizations',
      'template',
      'sort',
      'order',
      'finalizedFilter',
      'includeRolledOver',
      'includeHidden',
      'includeVisible',
      'page',
      'pageSize',
    ]
    return filterParams.some((param) => searchParams.has(param))
  }, [searchParams])

  // Load persisted state from localStorage if no URL params
  const persistedState = useMemo((): Partial<PersistedFilterState> => {
    if (hasUrlParams) return {}
    try {
      const stored = localStorage.getItem(FILTER_STORAGE_KEY)
      if (stored) {
        return JSON.parse(stored) as PersistedFilterState
      }
    } catch (e) {
      console.warn('Failed to parse persisted filter state:', e)
    }
    return {}
  }, [hasUrlParams])

  // URL params override localStorage
  const urlPeriodId = searchParams.get('periodId')
  const urlRootPeriodId = searchParams.get('rootPeriodId')
  const urlSearch = searchParams.get('search')
  const urlOrgs = searchParams.get('organizations')?.split(',').filter(Boolean) || []
  const urlTemplate = searchParams.get('template') || ''

  const [selectedPlans, setSelectedPlans] = useState<Set<string>>(new Set())
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false)
  const [rolloverDialogOpen, setRolloverDialogOpen] = useState(false)
  const [updateCompensationDialogOpen, setUpdateCompensationDialogOpen] = useState(false)
  const [bulkFinalizeEndDate, setBulkFinalizeEndDate] = useState<Dayjs | null>(null)
  const [singleFinalizeModalOpen, setSingleFinalizeModalOpen] = useState(false)
  const [selectedPlanToFinalize, setSelectedPlanToFinalize] = useState<PlanMetadata | null>(null)
  const [finalizeEndDate, setFinalizeEndDate] = useState<Dayjs | null>(null)
  const [archiveModalOpen, setArchiveModalOpen] = useState(false)
  const [selectedPlanToArchive, setSelectedPlanToArchive] = useState<PlanMetadata | null>(null)
  const [reopenModalOpen, setReopenModalOpen] = useState(false)
  const [selectedPlanToReopen, setSelectedPlanToReopen] = useState<PlanMetadata | null>(null)
  const [clearFinalizationModalOpen, setClearFinalizationModalOpen] = useState(false)
  const [selectedPlanToClearFinalization, setSelectedPlanToClearFinalization] = useState<PlanMetadata | null>(null)
  const [lifecycleInfoOpen, setLifecycleInfoOpen] = useState(false)
  const [changeOrganizationDialogOpen, setChangeOrganizationDialogOpen] = useState(false)
  const [selectedPlanToChangeOrganization, setSelectedPlanToChangeOrganization] = useState<PlanMetadata | null>(null)
  const [selectedOrganizationForPlanChange, setSelectedOrganizationForPlanChange] = useState('')

  // Rollover state
  const [rolloverModalOpen, setRolloverModalOpen] = useState(false)
  const [selectedPlanToRollover, setSelectedPlanToRollover] = useState<PlanMetadata | null>(null)
  const [rolloverPeriodIdLabelMap, setRolloverPeriodIdLabelMap] = useState<Record<string, string>>({})

  // State initialized from URL parameters, falling back to persisted state, then defaults
  const [search, setSearchState] = useState<string>(urlSearch || persistedState.search || '')
  const [selectedOrganizations, setSelectedOrganizationsState] = useState<string[]>(
    urlOrgs.length > 0 ? urlOrgs : persistedState.organizations || []
  )
  const [selectedTemplate, setSelectedTemplateState] = useState<string>(urlTemplate || persistedState.template || '')
  const [selectedPeriodId, setSelectedPeriodIdState] = useState<string>(urlPeriodId || persistedState.periodId || '')
  const [selectedRootPeriodId, setSelectedRootPeriodIdState] = useState<string>(
    urlRootPeriodId || persistedState.rootPeriodId || ''
  )
  const [sortBy, setSortByState] = useState<string>(
    searchParams.get('sort') || persistedState.sort || 'employeeLastName'
  )
  const [sortDirection, setSortDirectionState] = useState<'asc' | 'desc'>(
    (searchParams.get('order') as 'asc' | 'desc') || persistedState.order || 'asc'
  )
  const [finalizedFilter, setFinalizedFilterState] = useState<FinalizedFilter>(
    (searchParams.get('finalizedFilter') as FinalizedFilter) || persistedState.finalizedFilter || 'all'
  )
  const [includeRolledOver, setIncludeRolledOverState] = useState<boolean>(
    searchParams.has('includeRolledOver')
      ? searchParams.get('includeRolledOver') !== 'false'
      : (persistedState.includeRolledOver ?? true)
  )
  const [includeHidden, setIncludeHiddenState] = useState<boolean>(
    searchParams.has('includeHidden')
      ? searchParams.get('includeHidden') !== 'false'
      : (persistedState.includeHidden ?? true)
  )
  const [includeVisible, setIncludeVisibleState] = useState<boolean>(
    searchParams.has('includeVisible')
      ? searchParams.get('includeVisible') !== 'false'
      : (persistedState.includeVisible ?? true)
  )

  const [page, setPageState] = useState<number>(
    parseInt(searchParams.get('page') || '', 10) || persistedState.page || 1
  )
  const [pageSize, setPageSizeState] = useState<number>(
    parseInt(searchParams.get('pageSize') || '', 10) || persistedState.pageSize || 50
  )

  // Edit user dialog state
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false)
  const [selectedUserIdForEdit, setSelectedUserIdForEdit] = useState<string | null>(null)
  const [selectedOrgIdForEdit, setSelectedOrgIdForEdit] = useState<string | null>(null)

  // More Filters menu state
  const [moreFiltersAnchorEl, setMoreFiltersAnchorEl] = useState<null | HTMLElement>(null)
  const moreFiltersOpen = Boolean(moreFiltersAnchorEl)

  // Row actions menu state
  const [rowActionsAnchorEl, setRowActionsAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedPlanForActions, setSelectedPlanForActions] = useState<PlanMetadata | null>(null)
  const rowActionsOpen = Boolean(rowActionsAnchorEl)

  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })

  // Persist filter state to localStorage whenever it changes
  useEffect(() => {
    const filterState: PersistedFilterState = {
      periodId: selectedPeriodId,
      rootPeriodId: selectedRootPeriodId,
      search,
      organizations: selectedOrganizations,
      template: selectedTemplate,
      sort: sortBy,
      order: sortDirection,
      finalizedFilter,
      includeRolledOver,
      includeHidden,
      includeVisible,
      page,
      pageSize,
    }
    try {
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filterState))
    } catch (e) {
      console.warn('Failed to persist filter state:', e)
    }
  }, [
    selectedPeriodId,
    selectedRootPeriodId,
    search,
    selectedOrganizations,
    selectedTemplate,
    sortBy,
    sortDirection,
    finalizedFilter,
    includeRolledOver,
    includeHidden,
    includeVisible,
    page,
    pageSize,
  ])

  const rootOrganizationId = getRootOrganizationId(profile)

  // Get organization IDs where user has permission to hide/show plans
  const organizationIdsWithHiddenWrite = getOrganizationIdsWithPermissions(profile, [Permissions.PLANS_HIDDEN_WRITE])

  // Get organization IDs where user can update plan organization in place
  const organizationIdsWithUnpublishedManagerRead = getOrganizationIdsWithPermissions(profile, [
    Permissions.UNPUBLISHED_MANAGER_READ,
  ])

  const organizationNameMap = useMemo(() => {
    return profile.roles.reduce(
      (map, role) => {
        map[role.organization.id] = role.organization.name
        return map
      },
      {} as Record<string, string>
    )
  }, [profile.roles])

  // Load periods for selector
  const { periods: fetchedPeriods } = usePeriods(token!, null, !!token)

  // Get current period from selection - handle PeriodsResponse structure
  const periodsArray = fetchedPeriods?.periods || []
  const currentPeriod = periodsArray.find((p: Period) => p.id === selectedPeriodId)
  const currentRootPeriodId = currentPeriod?.rootPeriodId || selectedRootPeriodId

  // Load plans metadata
  const {
    plans,
    pagination,
    isLoadingPlansMetadata,
    isError: error,
    handleRefetch: refetchPlans,
    optimisticRemovePlans,
    optimisticUpdatePlans,
  } = usePlansMetadata(
    token,
    rootOrganizationId,
    currentRootPeriodId,
    selectedPeriodId || null,
    selectedOrganizations,
    selectedTemplate,
    search,
    sortBy,
    sortDirection,
    finalizedFilter,
    includeRolledOver,
    includeHidden,
    includeVisible,
    (page - 1) * pageSize, // offset
    pageSize, // limit
    !!token && !!rootOrganizationId && !!currentRootPeriodId && !!selectedPeriodId
  )

  // Fetch templates using the templates API based on the root period
  // We fetch templates for the entire root period, then filter by specific periodId in templateOptions
  const { templates, refetch: refetchTemplates } = useSearchOrganizationTemplates(token || '', {
    organizationIds:
      selectedOrganizations.length > 0 ? selectedOrganizations : rootOrganizationId ? [rootOrganizationId] : [],
    periodIds: currentRootPeriodId ? [currentRootPeriodId] : [],
    searchTerm: '',
    pagination: {
      page: 1,
      perPage: 1000,
    },
    sort: [
      {
        field: 'label',
        order: 'asc',
      },
    ],
  })

  // Functions to update both state and URL parameters
  const updateURLParams = useCallback(
    (updates: Record<string, string | string[] | boolean | null>) => {
      const newSearchParams = new URLSearchParams(searchParams)

      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
          newSearchParams.delete(key)
        } else if (Array.isArray(value)) {
          newSearchParams.set(key, value.join(','))
        } else {
          newSearchParams.set(key, String(value))
        }
      })

      setSearchParams(newSearchParams, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  const setSearch = useCallback(
    (value: string) => {
      setSearchState(value)
      setPageState(1) // Reset to first page when searching
      updateURLParams({ search: value, page: '1' })
    },
    [updateURLParams]
  )

  const setSelectedOrganizations = useCallback(
    (value: string[]) => {
      setSelectedOrganizationsState(value)
      setPageState(1) // Reset to first page when changing filters
      updateURLParams({ organizations: value, page: '1' })
      refetchTemplates()
    },
    [updateURLParams, refetchTemplates]
  )

  const setSelectedTemplate = useCallback(
    (value: string) => {
      setSelectedTemplateState(value)
      setPageState(1) // Reset to first page when changing filters
      updateURLParams({ template: value, page: '1' })
    },
    [updateURLParams]
  )

  const setSelectedPeriodId = useCallback(
    (value: string) => {
      setSelectedPeriodIdState(value)
      setPageState(1) // Reset to first page when changing filters
      updateURLParams({ periodId: value, page: '1' })
      refetchTemplates()
    },
    [updateURLParams, refetchTemplates]
  )

  const setSortBy = useCallback(
    (value: string) => {
      setSortByState(value)
      setPageState(1) // Reset to first page when changing sort
      updateURLParams({ sort: value, page: '1' })
    },
    [updateURLParams]
  )

  const setSortDirection = useCallback(
    (value: 'asc' | 'desc') => {
      setSortDirectionState(value)
      setPageState(1) // Reset to first page when changing sort
      updateURLParams({ order: value, page: '1' })
    },
    [updateURLParams]
  )

  const setFinalizedFilter = useCallback(
    (value: FinalizedFilter) => {
      setFinalizedFilterState(value)
      setPageState(1) // Reset to first page when changing filters
      updateURLParams({ finalizedFilter: value, page: '1' })
    },
    [updateURLParams]
  )

  const setIncludeRolledOver = useCallback(
    (value: boolean) => {
      setIncludeRolledOverState(value)
      setPageState(1) // Reset to first page when changing filters
      updateURLParams({ includeRolledOver: value, page: '1' })
    },
    [updateURLParams]
  )

  const setIncludeHidden = useCallback(
    (value: boolean) => {
      setIncludeHiddenState(value)
      setPageState(1) // Reset to first page when changing filters
      updateURLParams({ includeHidden: value, page: '1' })
    },
    [updateURLParams]
  )

  const setIncludeVisible = useCallback(
    (value: boolean) => {
      setIncludeVisibleState(value)
      setPageState(1) // Reset to first page when changing filters
      updateURLParams({ includeVisible: value, page: '1' })
    },
    [updateURLParams]
  )

  const setPage = useCallback(
    (value: number) => {
      setPageState(value)
      updateURLParams({ page: String(value) })
    },
    [updateURLParams]
  )

  const setPageSize = useCallback(
    (value: number) => {
      setPageSizeState(value)
      setPageState(1) // Reset to first page when changing page size
      updateURLParams({ pageSize: String(value), page: '1' })
    },
    [updateURLParams]
  )

  // Hook for finalizing plans
  const { closePlan, isLoading: isClosingPlan } = useClosePlan(token!)
  const { bulkClosePlans, isLoading: isBulkClosingPlans } = useBulkClosePlans(token!)
  const { archivePlan, isLoading: isArchivingPlan } = useArchivePlan(token!)
  const { reopenPlan, isLoading: isReopeningPlan } = useReopenPlan(token!)
  const { hidePlan, isLoading: isHidingPlan } = useHidePlan(token!)
  const { showPlan, isLoading: isShowingPlan } = useShowPlan(token!)
  const { updatePlanOrganization, isLoading: isUpdatingPlanOrganization } = useUpdatePlanOrganization(token!)
  const { clearFinalization, isLoading: isClearingFinalization } = useClearFinalization(token!)

  // Load periods for rollover functionality
  const {
    periods: rolloverPeriods,
    isLoadingPeriods: isLoadingRolloverPeriods,
    handleRefetch: refetchRolloverPeriods,
  } = usePeriods(
    token!,
    selectedPlanToRollover?.organizationId || null,
    !!token && !!selectedPlanToRollover?.organizationId
  )

  // Set default period when periods are loaded and no period is selected
  useEffect(() => {
    if (periodsArray.length > 0 && !selectedPeriodId) {
      const currentRootPeriodId = getCurrentRootPeriodId(periodsArray)
      if (currentRootPeriodId) {
        console.debug('Setting default period to current root period:', currentRootPeriodId)
        setSelectedPeriodIdState(currentRootPeriodId)
        setSelectedRootPeriodIdState(currentRootPeriodId)
        updateURLParams({
          periodId: currentRootPeriodId,
          rootPeriodId: currentRootPeriodId,
          // Set all templates (empty string = All)
          template: '',
          // Set all teams (empty array = all)
          organizations: [],
          // All other filters are already on by default
        })
      }
    }
  }, [periodsArray, selectedPeriodId, updateURLParams])

  // Set root period ID when periods are loaded and we have a selected period
  useEffect(() => {
    if (periodsArray.length > 0 && selectedPeriodId && !selectedRootPeriodId) {
      const period = findPeriodById(periodsArray, selectedPeriodId)
      if (period) {
        console.debug('Setting root period ID for:', period.label)
        const rootPeriodId = period.rootPeriodId || period.id || ''
        setSelectedRootPeriodIdState(rootPeriodId)
        updateURLParams({ rootPeriodId })
      }
    }
  }, [periodsArray, selectedPeriodId, selectedRootPeriodId, updateURLParams])

  // Create template options from templates API
  // Filter templates by the specific selected periodId (not rootPeriodId)
  const templateOptions = useMemo(() => {
    if (!templates || templates.length === 0) return [{ id: '', label: 'All' }]

    const templateOpts = templates
      .filter((template) => {
        // Only show templates that match the selected period ID and have required fields
        return template.id && template.label && template.periodId === selectedPeriodId
      })
      .map((template) => ({
        id: template.id || '',
        label: template.label,
      }))
    return [{ id: '', label: 'All' }, ...templateOpts]
  }, [templates, selectedPeriodId])

  // Backend handles all filtering including templates
  const filteredPlans = plans

  // Effect to update rollover period label map when rollover periods change
  useEffect(() => {
    if (rolloverPeriods?.periods) {
      setRolloverPeriodIdLabelMap(createMergedIdLabelMap(rolloverPeriods.periods))
    }
  }, [rolloverPeriods])

  // Reset template selection when period changes to ensure valid templates
  useEffect(() => {
    if (currentRootPeriodId && templateOptions.length > 0) {
      // Check if current selected template is still available for this period
      const isCurrentTemplateAvailable = templateOptions.some((t) => t.id === selectedTemplate)
      if (!isCurrentTemplateAvailable && selectedTemplate !== '') {
        // Reset to 'All' if current template is not available for the new period
        setSelectedTemplateState('')
        updateURLParams({ template: '' })
      }
    }
  }, [currentRootPeriodId, templateOptions, selectedTemplate, updateURLParams])

  // Update cached root period ID when period selection changes
  const handlePeriodChange = (periodId: string) => {
    console.debug('Period selection changed to:', periodId)
    const period = findPeriodById(periodsArray, periodId)
    if (period) {
      console.debug('Found period:', period.label, 'rootPeriodId:', period.rootPeriodId)
      const rootPeriodId = period.rootPeriodId || period.id || ''

      // Update both state and URL in a single operation
      setSelectedPeriodIdState(period.id || '')
      setSelectedRootPeriodIdState(rootPeriodId)
      updateURLParams({
        periodId: period.id || '',
        rootPeriodId: rootPeriodId,
      })

      // SWR will automatically fetch fresh data when dependencies change
      refetchTemplates()
    } else {
      console.warn('Period not found in periodsArray:', periodId)
    }
  }

  const handleSearch = useCallback(
    (value: string) => {
      setSearch(value)
    },
    [setSearch]
  )

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortDirection('asc')
    }
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked && filteredPlans) {
      const newSelected = new Set(filteredPlans.map((plan: PlanMetadata) => `${plan.id}-${plan.revision}`))
      setSelectedPlans(newSelected)
    } else {
      setSelectedPlans(new Set())
    }
  }

  const handleSelectPlan = (planId: string, revision: number, checked: boolean) => {
    const planKey = `${planId}-${revision}`
    const newSelected = new Set(selectedPlans)
    if (checked) {
      newSelected.add(planKey)
    } else {
      newSelected.delete(planKey)
    }
    setSelectedPlans(newSelected)
  }

  const handleCancelSelection = () => {
    setSelectedPlans(new Set())
  }

  const handleFinalizePlans = () => {
    // Set default date when opening the dialog
    const defaultDate = getDefaultFinalizeDate(selectedPeriodId)
    setBulkFinalizeEndDate(defaultDate)
    setFinalizeDialogOpen(true)
  }

  const handleRolloverPlans = () => {
    setRolloverDialogOpen(true)
  }

  const handleUpdateCompensation = () => {
    setUpdateCompensationDialogOpen(true)
  }

  const handleCloseUpdateCompensationDialog = () => {
    setUpdateCompensationDialogOpen(false)
  }

  const handleConfirmUpdateCompensation = () => {
    setUpdateCompensationDialogOpen(false)
    setSelectedPlans(new Set())
    refetchPlans()
    refetchTemplates()
  }

  const getSelectedPlansForUpdate = () => {
    return getSelectedPlanObjects().map((plan: PlanMetadata) => ({
      id: plan.id,
      revision: plan.revision,
      userId: plan.userId,
      templateId: plan.templateId || '',
      employeeFirstName: plan.employeeFirstName,
      employeeLastName: plan.employeeLastName,
      baseSalary: plan.baseSalary,
      position: plan.position,
      periodProration: plan.periodProration,
      compensationModel: plan.compensationModel,
      variablePercent: plan.variablePercent,
      variableCap: plan.variableCap,
      periodId: plan.periodId,
      organizationId: plan.organizationId,
      rootOrganizationId: plan.rootOrganizationId,
      rootPeriodId: plan.rootPeriodId,
    }))
  }

  const getSelectedPlansForRollover = () => {
    return getSelectedPlanObjects().map((plan: PlanMetadata) => ({
      id: plan.id,
      revision: plan.revision,
      userId: plan.userId,
      templateId: plan.templateId || '',
      employeeFirstName: plan.employeeFirstName,
      employeeLastName: plan.employeeLastName,
      baseSalary: plan.baseSalary,
      position: plan.position,
      periodProration: plan.periodProration,
      compensationModel: plan.compensationModel,
      variablePercent: plan.variablePercent,
      variableCap: plan.variableCap,
    }))
  }

  const handleFinalizeSinglePlan = (plan: PlanMetadata) => {
    setSelectedPlanToFinalize(plan)
    // Set default date when opening the dialog
    const defaultDate = getDefaultFinalizeDate(plan.periodId)
    setFinalizeEndDate(defaultDate)
    setSingleFinalizeModalOpen(true)
  }

  const handleCloseFinalizeModal = () => {
    setSingleFinalizeModalOpen(false)
    setSelectedPlanToFinalize(null)
    setFinalizeEndDate(null)
  }

  const handleConfirmFinalizePlan = async () => {
    if (!selectedPlanToFinalize || !finalizeEndDate || !rootOrganizationId) return

    const planToFinalize = selectedPlanToFinalize
    const dateString = finalizeEndDate.format('YYYY-MM-DD')
    const isoString = dayjs.utc(dateString).format('YYYY-MM-DDTHH:mm:ss.SSSZ')

    // Close modal and clear selection immediately for better UX
    setSingleFinalizeModalOpen(false)
    setSelectedPlanToFinalize(null)
    setFinalizeEndDate(null)

    // Optimistically update the plan with the end date
    optimisticUpdatePlans([{ id: planToFinalize.id, changes: { endDate: isoString } }])

    try {
      await closePlan({
        planId: planToFinalize.id,
        endDate: isoString,
        rootOrganizationId: rootOrganizationId,
      } as ClosePlanParams)

      setSnackbar({
        open: true,
        message: `Plan for ${planToFinalize.employeeFirstName} ${planToFinalize.employeeLastName} finalized successfully`,
        severity: 'success',
      })
    } catch (error) {
      console.error('Error finalizing plan:', error)
      setSnackbar({
        open: true,
        message: 'Failed to finalize the plan',
        severity: 'error',
      })
    }

    // Always refetch to reflect changes
    refetchPlans()
    refetchTemplates()
  }

  const handleRolloverSinglePlan = (plan: PlanMetadata) => {
    setSelectedPlanToRollover(plan)

    // Fetch periods for the selected plan's organization
    if (plan.organizationId) {
      refetchRolloverPeriods()
    }

    setRolloverModalOpen(true)
  }

  const handleCloseRolloverModal = () => {
    setRolloverModalOpen(false)
    setSelectedPlanToRollover(null)
    // Refresh data when closing modal to ensure we have latest state
    refetchPlans()
    refetchTemplates()
  }

  const handleConfirmSingleRollover = (periodId: string) => {
    console.debug('Rolling over plan with ID:', selectedPlanToRollover?.id, 'to period:', periodId)

    // Optimistically update the plan to mark it as rolled over
    if (selectedPlanToRollover) {
      optimisticUpdatePlans([{ id: selectedPlanToRollover.id, changes: { isRolledOver: true } }])
    }

    // Close modal and refresh data (success message is shown by the dialog)
    setRolloverModalOpen(false)
    setSelectedPlanToRollover(null)
    refetchPlans()
    refetchTemplates()
  }

  const handleArchiveSinglePlan = (plan: PlanMetadata) => {
    setSelectedPlanToArchive(plan)
    setArchiveModalOpen(true)
  }

  const handleCloseArchiveModal = () => {
    setArchiveModalOpen(false)
    setSelectedPlanToArchive(null)
  }

  const handleConfirmArchivePlan = async () => {
    if (!selectedPlanToArchive || !rootOrganizationId) return

    const planToArchive = selectedPlanToArchive

    // Close modal and clear selection immediately for better UX
    setArchiveModalOpen(false)
    setSelectedPlanToArchive(null)

    // Optimistically remove the plan from the list
    optimisticRemovePlans([planToArchive.id])

    try {
      await archivePlan({
        planId: planToArchive.id,
        rootOrganizationId: rootOrganizationId,
      })

      setSnackbar({
        open: true,
        message: `Plan for ${planToArchive.employeeFirstName} ${planToArchive.employeeLastName} archived successfully`,
        severity: 'success',
      })
    } catch (error) {
      console.error('Error archiving plan:', error)
      setSnackbar({
        open: true,
        message: 'Failed to archive the plan',
        severity: 'error',
      })
    }

    // Always refetch to reflect changes
    refetchPlans()
    refetchTemplates()
  }

  const handleReopenSinglePlan = (plan: PlanMetadata) => {
    setSelectedPlanToReopen(plan)
    setReopenModalOpen(true)
  }

  const handleCloseReopenModal = () => {
    setReopenModalOpen(false)
    setSelectedPlanToReopen(null)
  }

  const handleConfirmReopenPlan = async () => {
    if (!selectedPlanToReopen || !rootOrganizationId) return

    const planToReopen = selectedPlanToReopen

    // Close modal and clear selection immediately for better UX
    setReopenModalOpen(false)
    setSelectedPlanToReopen(null)

    // Optimistically update the plan to remove the end date
    optimisticUpdatePlans([{ id: planToReopen.id, changes: { endDate: undefined } }])

    try {
      await reopenPlan({
        planId: planToReopen.id,
        rootOrganizationId: rootOrganizationId,
      })

      setSnackbar({
        open: true,
        message: `Plan for ${planToReopen.employeeFirstName} ${planToReopen.employeeLastName} reopened successfully`,
        severity: 'success',
      })
    } catch (error) {
      console.error('Error reopening plan:', error)
      setSnackbar({
        open: true,
        message: 'Failed to reopen the plan',
        severity: 'error',
      })
    }

    // Always refetch to reflect changes
    refetchPlans()
    refetchTemplates()
  }

  // Clear Finalization handlers
  const handleClearFinalization = (plan: PlanMetadata) => {
    setSelectedPlanToClearFinalization(plan)
    setClearFinalizationModalOpen(true)
  }

  const handleCloseClearFinalizationModal = () => {
    setClearFinalizationModalOpen(false)
    setSelectedPlanToClearFinalization(null)
  }

  const handleConfirmClearFinalization = async () => {
    if (!selectedPlanToClearFinalization) return

    const planToClear = selectedPlanToClearFinalization

    setClearFinalizationModalOpen(false)
    setSelectedPlanToClearFinalization(null)

    optimisticUpdatePlans([{ id: planToClear.id, changes: { finalizedAt: undefined } }])

    try {
      await clearFinalization({
        planId: planToClear.id,
        planRevision: planToClear.revision,
      })

      setSnackbar({
        open: true,
        message: `Finalization cleared for ${planToClear.employeeFirstName} ${planToClear.employeeLastName}`,
        severity: 'success',
      })
    } catch (error) {
      console.error('Error clearing finalization:', error)
      setSnackbar({
        open: true,
        message: 'Failed to clear finalization',
        severity: 'error',
      })
    }

    refetchPlans()
    refetchTemplates()
  }

  // Permission helper for clear finalization (same as Change Plan Team)
  const canClearFinalization = (plan: PlanMetadata) => {
    return organizationIdsWithUnpublishedManagerRead.includes(plan.organizationId)
  }

  const confirmFinalize = async () => {
    if (!bulkFinalizeEndDate || !rootOrganizationId || selectedPlans.size === 0) return

    const dateString = bulkFinalizeEndDate.format('YYYY-MM-DD')
    const isoString = dayjs.utc(dateString).format('YYYY-MM-DDTHH:mm:ss.SSSZ')

    const selectedPlanObjects = getSelectedPlanObjects()
    const planIds = selectedPlanObjects.map((plan: PlanMetadata) => ({
      planId: plan.id,
      planRevision: plan.revision,
    }))

    // Close dialog immediately for better UX
    setFinalizeDialogOpen(false)
    setBulkFinalizeEndDate(null)

    // Clear selections immediately
    setSelectedPlans(new Set())

    // Optimistically update all selected plans with the end date
    optimisticUpdatePlans(selectedPlanObjects.map((plan) => ({ id: plan.id, changes: { endDate: isoString } })))

    try {
      const response = await bulkClosePlans({
        planIds,
        rootOrganizationId,
        endDate: isoString,
      })

      const { successful, failed } = response.summary
      let message = ''
      let severity: 'success' | 'error' = 'success'

      if (failed === 0) {
        message = `Successfully finalized ${successful} plan${successful !== 1 ? 's' : ''}`
        severity = 'success'
      } else if (successful === 0) {
        message = `Failed to finalize all ${failed} plan${failed !== 1 ? 's' : ''}`
        severity = 'error'
      } else {
        message = `Finalized ${successful} plan${successful !== 1 ? 's' : ''}, failed to finalize ${failed}`
        severity = 'error'
      }

      setSnackbar({
        open: true,
        message,
        severity,
      })
    } catch (error) {
      console.error('Error finalizing plans:', error)
      setSnackbar({
        open: true,
        message: 'Failed to finalize plans',
        severity: 'error',
      })
    }

    // Always refetch to reflect changes
    refetchPlans()
    refetchTemplates()
  }

  const handleConfirmRollover = (periodId: string) => {
    console.debug('Rolling over plans to period:', periodId)

    // Optimistically update all selected plans to mark them as rolled over
    const selectedPlanObjects = getSelectedPlanObjects()
    optimisticUpdatePlans(selectedPlanObjects.map((plan) => ({ id: plan.id, changes: { isRolledOver: true } })))

    // Close modal and refresh data (success message is shown by the dialog)
    setRolloverDialogOpen(false)
    setSelectedPlans(new Set())
    refetchPlans()
    refetchTemplates()
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return formatDisplayDate(dateString)
  }

  // Helper function to check if a date should be disabled (outside period range)
  const shouldDisableDateForPeriod = (date: Dayjs, periodId: string): boolean => {
    if (!periodId) return true

    const period = findPeriodById(periodsArray, periodId)
    if (!period) return true

    const periodStart = parsePeriodDate(period.startDate)
    const periodEnd = parsePeriodDate(period.endDate)
    const checkDate = date.toDate()

    return checkDate < periodStart || checkDate > periodEnd
  }

  // Helper function to get period date range for display
  const getPeriodDateRangeText = (periodId: string): string => {
    const period = findPeriodById(periodsArray, periodId)
    if (!period) return ''

    const periodStart = parsePeriodDate(period.startDate)
    const periodEnd = parsePeriodDate(period.endDate)

    return `${formatDisplayDate(periodStart)} - ${formatDisplayDate(periodEnd)}`
  }

  // Helper function to get default finalize date (earliest of today and period end date)
  const getDefaultFinalizeDate = (periodId: string): Dayjs | null => {
    if (!periodId) return null

    const period = findPeriodById(periodsArray, periodId)
    if (!period) return null

    const today = dayjs()
    const periodEnd = dayjs(parsePeriodDate(period.endDate))

    // Return the earlier of today or period end date
    return today.isBefore(periodEnd) ? today : periodEnd
  }

  const isAllSelected = filteredPlans && selectedPlans.size === filteredPlans.length
  const isIndeterminate = selectedPlans.size > 0 && selectedPlans.size < (filteredPlans?.length || 0)

  // Helper functions to check selected plan states
  const getSelectedPlanObjects = () => {
    if (!filteredPlans) return []
    return filteredPlans.filter((plan: PlanMetadata) => selectedPlans.has(`${plan.id}-${plan.revision}`))
  }

  const hasSelectedFinalizedPlans = () => {
    return getSelectedPlanObjects().some((plan: PlanMetadata) => !!plan.endDate)
  }

  const hasSelectedMixedTemplates = () => {
    const selectedPlanObjects = getSelectedPlanObjects()
    if (selectedPlanObjects.length <= 1) return false

    const templateIds = new Set(selectedPlanObjects.map((plan: PlanMetadata) => plan.templateId))
    return templateIds.size > 1
  }

  // Helper function to check if a plan is waiting for finalization
  const isPlanWaitingForFinalization = (plan: PlanMetadata): boolean => {
    if (!plan.endDate) return false
    if (plan.finalizedAt) return false

    const endDate = new Date(plan.endDate)
    const today = new Date()
    today.setHours(0, 0, 0, 0) // Reset time to compare dates only

    return endDate < today
  }

  // Helper functions for hide/show functionality
  const canBulkHideOrShow = () => {
    const selectedPlanObjects = getSelectedPlanObjects()
    if (selectedPlanObjects.length === 0) return false

    // Check if all selected plans belong to organizations where user has PLANS_HIDDEN_WRITE permission
    if (!selectedPlanObjects.every((plan) => organizationIdsWithHiddenWrite.includes(plan.organizationId))) {
      return false
    }

    // Check if all selected plans have the same hidden status
    const allHidden = selectedPlanObjects.every((plan) => plan.isHidden)
    const allVisible = selectedPlanObjects.every((plan) => !plan.isHidden)

    // Can only show button if all plans are hidden OR all plans are visible (not mixed)
    return allHidden || allVisible
  }

  const shouldShowHideButton = () => {
    const selectedPlanObjects = getSelectedPlanObjects()
    if (selectedPlanObjects.length === 0) return false

    // Show "Make Hidden" button if all selected plans are visible
    return selectedPlanObjects.every((plan) => !plan.isHidden)
  }

  const handleBulkHideOrShowPlans = async () => {
    if (!rootOrganizationId || selectedPlans.size === 0) return

    const selectedPlanObjects = getSelectedPlanObjects()
    const isHiding = shouldShowHideButton()
    const action = isHiding ? hidePlan : showPlan
    const actionName = isHiding ? 'hide' : 'show'

    const planIds = selectedPlanObjects.map((plan) => ({
      planId: plan.id,
      planRevision: plan.revision,
    }))

    // Clear selections immediately for better UX
    setSelectedPlans(new Set())

    // Optimistically update all selected plans with the new hidden status
    optimisticUpdatePlans(selectedPlanObjects.map((plan) => ({ id: plan.id, changes: { isHidden: isHiding } })))

    try {
      const response = await action({
        planIds,
        rootOrganizationId,
      })

      const successful = response.summary.successful
      const failed = response.summary.failed

      let message = ''
      let severity: 'success' | 'error' = 'success'

      if (failed === 0) {
        message = `Successfully ${actionName === 'hide' ? 'hidden' : 'made visible'} ${successful} plan${successful !== 1 ? 's' : ''}`
        severity = 'success'
      } else if (successful === 0) {
        message = `Failed to ${actionName} all ${failed} plan${failed !== 1 ? 's' : ''}`
        severity = 'error'
      } else {
        message = `${actionName === 'hide' ? 'Hidden' : 'Made visible'} ${successful} plan${successful !== 1 ? 's' : ''}, failed to ${actionName} ${failed}`
        severity = 'error'
      }

      setSnackbar({
        open: true,
        message,
        severity,
      })
    } catch (error) {
      console.error(`Error ${actionName}ing plans:`, error)
      setSnackbar({
        open: true,
        message: `Failed to ${actionName} plans`,
        severity: 'error',
      })
    }

    // Always refetch to reflect changes
    refetchPlans()
    refetchTemplates()
  }

  const handleEditUserClick = (plan: PlanMetadata) => {
    setSelectedUserIdForEdit(plan.userId)
    setSelectedOrgIdForEdit(plan.organizationId)
    setEditUserDialogOpen(true)
  }

  const canUpdatePlanOrganization = (plan: PlanMetadata) => {
    return organizationIdsWithUnpublishedManagerRead.includes(plan.organizationId)
  }

  const hasMemberOrganizationMismatch = (plan: PlanMetadata) => {
    if (!plan.memberOrganizationId) {
      return false
    }

    return plan.memberOrganizationId !== plan.organizationId
  }

  const getOrganizationLabel = (organizationId?: string | null) => {
    if (!organizationId) {
      return 'Unknown team'
    }

    return organizationNameMap[organizationId] || organizationId
  }

  const handleOpenChangeOrganizationDialog = (plan: PlanMetadata) => {
    setSelectedPlanToChangeOrganization(plan)
    setSelectedOrganizationForPlanChange(plan.organizationId)
    setChangeOrganizationDialogOpen(true)
  }

  const handleOpenRowActionsMenu = (event: React.MouseEvent<HTMLElement>, plan: PlanMetadata) => {
    setRowActionsAnchorEl(event.currentTarget)
    setSelectedPlanForActions(plan)
  }

  const handleCloseRowActionsMenu = () => {
    setRowActionsAnchorEl(null)
    setSelectedPlanForActions(null)
  }

  const handleCloseChangeOrganizationDialog = () => {
    setChangeOrganizationDialogOpen(false)
    setSelectedPlanToChangeOrganization(null)
    setSelectedOrganizationForPlanChange('')
  }

  const handleConfirmPlanOrganizationChange = async () => {
    if (!selectedPlanToChangeOrganization || !selectedOrganizationForPlanChange) {
      return
    }

    if (selectedOrganizationForPlanChange === selectedPlanToChangeOrganization.organizationId) {
      handleCloseChangeOrganizationDialog()
      return
    }

    try {
      await updatePlanOrganization({
        planId: selectedPlanToChangeOrganization.id,
        planRevision: selectedPlanToChangeOrganization.revision,
        organizationId: selectedOrganizationForPlanChange,
      })

      optimisticUpdatePlans([
        {
          id: selectedPlanToChangeOrganization.id,
          changes: {
            organizationId: selectedOrganizationForPlanChange,
          },
        },
      ])

      setSnackbar({
        open: true,
        message: `Updated plan team for ${selectedPlanToChangeOrganization.employeeFirstName} ${selectedPlanToChangeOrganization.employeeLastName}`,
        severity: 'success',
      })

      handleCloseChangeOrganizationDialog()
    } catch (error) {
      console.error('Error updating plan organization:', error)
      setSnackbar({
        open: true,
        message: 'Failed to update plan team',
        severity: 'error',
      })
    }

    refetchPlans()
    refetchTemplates()
  }

  const handleCloseEditUserDialog = () => {
    setEditUserDialogOpen(false)
    setSelectedUserIdForEdit(null)
    setSelectedOrgIdForEdit(null)
  }

  return (
    <>
      <Grid container pb={2} alignItems="center">
        <Grid size={{ xs: 12 }}>
          <SimpleTitle title="Plans" />
        </Grid>
      </Grid>

      {/* Filter row */}
      <Grid container spacing={2} mb={3} alignItems="center">
        <Grid size={{ xs: 3 }}>
          <SearchInput
            id="plan-search-input"
            key="plan-search-input"
            onSearch={handleSearch}
            initialValue={search}
            labelBackgroundColor={theme.palette.background.default}
          />
        </Grid>
        <Grid size={{ xs: 9 }}>
          <Box display="flex" gap={2} justifyContent="flex-end" alignItems="center">
            <TemplateSelector
              templateOptions={templateOptions}
              selectedTemplate={selectedTemplate}
              onTemplateChange={setSelectedTemplate}
            />
            <SinglePeriodSelector
              periods={periodsArray}
              onChange={handlePeriodChange}
              value={selectedPeriodId}
              label="Filter Period"
              labelBackgroundColor={theme.palette.background.default}
              width="200px"
              pill
              noTruncate
            />
            <MultiOrganizationSelector
              userProfile={profile!}
              selectedOrganizationIds={selectedOrganizations}
              handleChange={setSelectedOrganizations}
              selectAll
              labelBackgroundColor={theme.palette.background.default}
              width="300px"
            />
            <Tooltip title="More Filters">
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FilterOutlined />}
                onClick={(e) => setMoreFiltersAnchorEl(e.currentTarget)}
                sx={{ height: '40px', minWidth: '140px' }}
              >
                More Filters
              </Button>
            </Tooltip>
            <Menu
              anchorEl={moreFiltersAnchorEl}
              open={moreFiltersOpen}
              onClose={() => setMoreFiltersAnchorEl(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: { minWidth: '280px', mt: 1 },
              }}
            >
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary' }}>
                  Visibility Filters
                </Typography>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeHidden}
                        onChange={(e) => setIncludeHidden(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Show Hidden"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeVisible}
                        onChange={(e) => setIncludeVisible(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Show Visible"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </Stack>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary' }}>
                  Status Filters
                </Typography>
                <Stack spacing={2}>
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel shrink variant="outlined">
                      Finalized Plans
                    </InputLabel>
                    <Select
                      labelId="finalized-filter-label"
                      variant="outlined"
                      value={finalizedFilter}
                      label="Finalized Plans"
                      style={{ borderRadius: '21px' }}
                      onChange={(e) => setFinalizedFilter(e.target.value as FinalizedFilter)}
                      displayEmpty
                    >
                      <MenuItem value="all">All Plans</MenuItem>
                      <MenuItem value="open">Open Only</MenuItem>
                      <MenuItem value="beforePeriodEnd">Closed Before Period End</MenuItem>
                      <MenuItem value="atPeriodEnd">Closed At Period End</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeRolledOver}
                        onChange={(e) => setIncludeRolledOver(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Include Rolled Over"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </Stack>
              </Box>
            </Menu>
            <Tooltip title="Clear Filters">
              <IconButton
                color="primary"
                onClick={() => {
                  setSelectedOrganizations([])
                  setSelectedTemplate('')
                  setSelectedPeriodId('')
                  setSearch('')
                  setFinalizedFilter('all')
                  setIncludeRolledOver(true)
                  setIncludeHidden(true)
                  setIncludeVisible(true)
                  setPage(1)
                  setPageSize(50) // Reset to default page size
                  // Clear persisted filter state
                  localStorage.removeItem(FILTER_STORAGE_KEY)
                  // Clear all URL parameters except authentication-related ones
                  updateURLParams({
                    organizations: [],
                    template: '',
                    periodId: '',
                    rootPeriodId: '',
                    search: '',
                    finalizedFilter: 'all',
                    includeRolledOver: true,
                    includeHidden: true,
                    includeVisible: true,
                    page: '1',
                    pageSize: '50',
                    sort: 'employeeLastName',
                    order: 'asc',
                  })
                }}
              >
                <CloseCircleOutlined />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>

      {error ? (
        <Box component={Paper} sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" color="error" mb={1}>
            Error Loading Plans
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Failed to load compensation plans for this period.
          </Typography>
        </Box>
      ) : isLoadingPlansMetadata ? (
        <Grid container spacing={12}>
          <Grid
            size={{ xs: 12 }}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CircularProgress color="primary" />
            Loading
          </Grid>
        </Grid>
      ) : (
        <Paper>
          <TableContainer
            sx={{
              width: '100%',
            }}
          >
            <Table stickyHeader aria-label="sticky table" size="small">
              <TableHead
                sx={{
                  '& th': {
                    borderTop: `1px solid ${theme.palette.divider}`,
                    borderBottom: `2px solid ${theme.palette.divider} !important`,
                  },
                }}
              >
                <TableRow>
                  <TableCell sx={{ position: 'sticky !important', width: 50, minWidth: 50 }} padding="checkbox">
                    <Checkbox
                      indeterminate={isIndeterminate}
                      checked={isAllSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </TableCell>
                  <TableCell sx={{ position: 'sticky !important', width: 200, minWidth: 200 }}>
                    <TableSortLabel
                      active={sortBy === 'employeeLastName'}
                      direction={sortBy === 'employeeLastName' ? sortDirection : 'asc'}
                      onClick={() => handleSort('employeeLastName')}
                    >
                      Employee
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ position: 'sticky !important', width: 150, minWidth: 150 }}>
                    <TableSortLabel
                      active={sortBy === 'templateLabel'}
                      direction={sortBy === 'templateLabel' ? sortDirection : 'asc'}
                      onClick={() => handleSort('templateLabel')}
                    >
                      Template
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ position: 'sticky !important', width: 120, minWidth: 120 }}>
                    <TableSortLabel
                      active={sortBy === 'baseSalary'}
                      direction={sortBy === 'baseSalary' ? sortDirection : 'asc'}
                      onClick={() => handleSort('baseSalary')}
                    >
                      {/* Use first plan's compensation model for column header, fallback to 'Base Salary' */}
                      {filteredPlans && filteredPlans.length > 0 && filteredPlans[0].compensationModel === 'BASE_PLUS'
                        ? 'Base/Total Compensation'
                        : 'Base Compensation'}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ position: 'sticky !important', width: 100, minWidth: 100 }}>
                    <TableSortLabel
                      active={sortBy === 'variablePercent'}
                      direction={sortBy === 'variablePercent' ? sortDirection : 'asc'}
                      onClick={() => handleSort('variablePercent')}
                    >
                      Variable %
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ position: 'sticky !important', width: 120, minWidth: 120 }}>
                    <TableSortLabel
                      active={sortBy === 'effectiveDate'}
                      direction={sortBy === 'effectiveDate' ? sortDirection : 'asc'}
                      onClick={() => handleSort('effectiveDate')}
                    >
                      Effective Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ position: 'sticky !important', width: 100, minWidth: 100 }}>
                    <TableSortLabel
                      active={sortBy === 'endDate'}
                      direction={sortBy === 'endDate' ? sortDirection : 'asc'}
                      onClick={() => handleSort('endDate')}
                    >
                      End Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ position: 'sticky !important', width: 120, minWidth: 120 }}>Finalized</TableCell>
                  <TableCell sx={{ position: 'sticky !important', width: 120, minWidth: 120 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(filteredPlans || []).map((plan: PlanMetadata) => {
                  const planKey = `${plan.id}-${plan.revision}`
                  const isSelected = selectedPlans.has(planKey)

                  return (
                    <TableRow
                      key={planKey}
                      role="checkbox"
                      tabIndex={-1}
                      sx={{
                        color: plan.isHidden ? theme.palette.text.disabled : 'inherit',
                        '& .MuiTableCell-root': {
                          color: plan.isHidden ? theme.palette.text.disabled : 'inherit',
                        },
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) => handleSelectPlan(plan.id, plan.revision, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell style={{ width: 200 }}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {plan.employeeFirstName} {plan.employeeLastName}
                          {hasMemberOrganizationMismatch(plan) && (
                            <Tooltip title="Plan does not match their current team.">
                              <WarningOutlined style={{ color: theme.palette.warning.main, fontSize: '14px' }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell style={{ width: 150 }}>{plan.templateLabel || '-'}</TableCell>
                      <TableCell style={{ width: 120 }}>
                        {formatCurrency(plan.baseSalary * plan.periodProration)}
                      </TableCell>
                      <TableCell style={{ width: 100 }}>{(plan.variablePercent * 100).toFixed(1)}%</TableCell>
                      <TableCell style={{ width: 120 }}>{formatDate(plan.effectiveDate)}</TableCell>
                      <TableCell style={{ width: 100 }}>
                        {plan.endDate ? (
                          <Tooltip
                            title={
                              isPlanWaitingForFinalization(plan)
                                ? 'Plan is waiting for all actuals to be submitted for the plan to be finalized'
                                : ''
                            }
                          >
                            <Box display="flex" alignItems="center" gap={0.5}>
                              {formatDate(plan.endDate)}
                              {isPlanWaitingForFinalization(plan) && (
                                <WarningOutlined style={{ color: theme.palette.warning.main, fontSize: '16px' }} />
                              )}
                            </Box>
                          </Tooltip>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell style={{ width: 120 }}>
                        {plan.finalizedAt ? formatDate(plan.finalizedAt) : '-'}
                      </TableCell>
                      <TableCell style={{ width: 120 }}>
                        <Tooltip title="Plan Actions">
                          <IconButton
                            size="medium"
                            sx={{ color: theme.palette.primary.main }}
                            onClick={(event) => handleOpenRowActionsMenu(event, plan)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Menu
            anchorEl={rowActionsAnchorEl}
            open={rowActionsOpen}
            onClose={handleCloseRowActionsMenu}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {selectedPlanForActions && (
              <>
                {/* ── View ── */}
                <Typography variant="overline" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary' }}>
                  View
                </Typography>
                <MenuItem
                  onClick={() => {
                    const actionPlan = selectedPlanForActions
                    handleCloseRowActionsMenu()
                    startTransition(() => {
                      navigate(`/employee/${actionPlan.userId}/compplan`)
                    })
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <EyeOutlined />
                    View Employee Compensation Plan
                  </Box>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    const actionPlan = selectedPlanForActions
                    handleCloseRowActionsMenu()
                    handleEditUserClick(actionPlan)
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <UserOutlined />
                    Edit Employee
                  </Box>
                </MenuItem>

                <Divider sx={{ my: 0.5 }} />

                {/* ── Status ── */}
                <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 0.5 }}>
                  <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                    Status
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setLifecycleInfoOpen(true)}
                    sx={{ color: 'text.secondary', p: 0 }}
                  >
                    <InfoCircleOutlined style={{ fontSize: '14px' }} />
                  </IconButton>
                </Box>
                <Tooltip title={selectedPlanForActions.endDate ? 'Plan already has an end date' : ''}>
                  <span>
                    <MenuItem
                      disabled={!!selectedPlanForActions.endDate}
                      onClick={() => {
                        const actionPlan = selectedPlanForActions
                        handleCloseRowActionsMenu()
                        handleFinalizeSinglePlan(actionPlan)
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <BookOutlined />
                        Close Plan
                      </Box>
                    </MenuItem>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    !selectedPlanForActions.endDate
                      ? 'Plan has no end date'
                      : selectedPlanForActions.finalizedAt
                        ? 'Must clear finalization first'
                        : currentPeriod?.isClosed
                          ? 'Cannot reopen — period is closed'
                          : ''
                  }
                >
                  <span>
                    <MenuItem
                      disabled={
                        !selectedPlanForActions.endDate ||
                        !!selectedPlanForActions.finalizedAt ||
                        !currentPeriod ||
                        currentPeriod.isClosed
                      }
                      onClick={() => {
                        const actionPlan = selectedPlanForActions
                        handleCloseRowActionsMenu()
                        handleReopenSinglePlan(actionPlan)
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <ReloadOutlined />
                        Reopen Plan
                      </Box>
                    </MenuItem>
                  </span>
                </Tooltip>
                {canClearFinalization(selectedPlanForActions) && (
                  <Tooltip title={!selectedPlanForActions.finalizedAt ? 'Plan is not finalized' : ''}>
                    <span>
                      <MenuItem
                        disabled={!selectedPlanForActions.finalizedAt}
                        onClick={() => {
                          const actionPlan = selectedPlanForActions
                          handleCloseRowActionsMenu()
                          handleClearFinalization(actionPlan)
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <CheckCircleOutlined />
                          Clear Finalization
                        </Box>
                      </MenuItem>
                    </span>
                  </Tooltip>
                )}

                <Divider sx={{ my: 0.5 }} />

                {/* ── Move ── */}
                <Typography variant="overline" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary' }}>
                  Move
                </Typography>
                {canUpdatePlanOrganization(selectedPlanForActions) && (
                  <MenuItem
                    onClick={() => {
                      const actionPlan = selectedPlanForActions
                      handleCloseRowActionsMenu()
                      handleOpenChangeOrganizationDialog(actionPlan)
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <SwapOutlined />
                      Change Plan Team
                    </Box>
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    const actionPlan = selectedPlanForActions
                    handleCloseRowActionsMenu()
                    handleRolloverSinglePlan(actionPlan)
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <FileSyncOutlined />
                    Rollover Plan
                  </Box>
                </MenuItem>

                <Divider sx={{ my: 0.5 }} />

                {/* ── Danger ── */}
                <Typography variant="overline" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary' }}>
                  Danger
                </Typography>
                <MenuItem
                  onClick={() => {
                    const actionPlan = selectedPlanForActions
                    handleCloseRowActionsMenu()
                    handleArchiveSinglePlan(actionPlan)
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} sx={{ color: 'error.main' }}>
                    <DeleteOutlined />
                    Archive Plan
                  </Box>
                </MenuItem>
              </>
            )}
          </Menu>

          {(!filteredPlans || filteredPlans.length === 0) && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="text.secondary" mb={1}>
                No Plans Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No compensation plans are available for this period.
              </Typography>
            </Box>
          )}

          {/* Pagination Controls */}
          {pagination && (
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={pagination.totalItems || 0}
              rowsPerPage={pageSize}
              page={page - 1} // TablePagination uses 0-based indexing
              onPageChange={(_, newPage) => setPage(newPage + 1)} // Convert back to 1-based
              onRowsPerPageChange={(event) => {
                const newPageSize = parseInt(event.target.value, 10)
                // Batch both pageSize and page updates to prevent multiple API calls
                setPageSizeState(newPageSize)
                setPageState(1)
                updateURLParams({ pageSize: String(newPageSize), page: '1' })
              }}
            />
          )}
        </Paper>
      )}

      {/* Change Plan Team Dialog */}
      <Dialog open={changeOrganizationDialogOpen} onClose={handleCloseChangeOrganizationDialog} fullWidth maxWidth="sm">
        <DialogTitle>Change Plan Team</DialogTitle>
        <DialogContent>
          {selectedPlanToChangeOrganization && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography>
                Update the plan team for{' '}
                <strong>
                  {selectedPlanToChangeOrganization.employeeFirstName}{' '}
                  {selectedPlanToChangeOrganization.employeeLastName}
                </strong>
                .
              </Typography>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  Current plan team:{' '}
                  <strong>{getOrganizationLabel(selectedPlanToChangeOrganization.organizationId)}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Current member team:{' '}
                  <strong>
                    {selectedPlanToChangeOrganization.memberOrganizationName ||
                      getOrganizationLabel(selectedPlanToChangeOrganization.memberOrganizationId)}
                  </strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Period:{' '}
                  <strong>
                    {findPeriodById(periodsArray, selectedPlanToChangeOrganization.periodId)?.label ||
                      selectedPlanToChangeOrganization.periodId}
                  </strong>
                </Typography>
              </Box>

              {hasMemberOrganizationMismatch(selectedPlanToChangeOrganization) && (
                <Alert severity="warning">Plan does not match their current team.</Alert>
              )}

              <SingleOrganizationSelector
                userProfile={profile}
                selectedOrganizationId={selectedOrganizationForPlanChange}
                handleChange={setSelectedOrganizationForPlanChange}
                label="New Team"
                placeholder="Select Team"
                width="100%"
                labelBackgroundColor={theme.palette.background.paper}
                disableAutoSelection
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChangeOrganizationDialog} disabled={isUpdatingPlanOrganization}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmPlanOrganizationChange}
            color="primary"
            variant="contained"
            disabled={
              !selectedPlanToChangeOrganization ||
              !selectedOrganizationForPlanChange ||
              selectedOrganizationForPlanChange === selectedPlanToChangeOrganization.organizationId ||
              isUpdatingPlanOrganization
            }
            startIcon={isUpdatingPlanOrganization ? <CircularProgress size={20} /> : null}
          >
            Update Team
          </Button>
        </DialogActions>
      </Dialog>

      {/* Finalize Dialog */}
      <Dialog open={finalizeDialogOpen} onClose={() => setFinalizeDialogOpen(false)}>
        <DialogTitle>Finalize Selected Plans</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Finalizing <strong>{selectedPlans.size}</strong> selected compensation plan
            {selectedPlans.size !== 1 ? 's' : ''}. This action cannot be undone.
          </Typography>
          <Typography sx={{ mb: 1 }}>
            To finalize these plans, please select an <strong>End Date</strong> below.
          </Typography>
          {selectedPeriodId && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              End date must be within the period range: <strong>{getPeriodDateRangeText(selectedPeriodId)}</strong>
            </Typography>
          )}
          <DesktopDatePicker
            label="End Date"
            value={bulkFinalizeEndDate}
            onChange={(newValue: Dayjs | null) => {
              if (newValue) {
                const dateString = newValue.format('YYYY-MM-DD')
                const utcDate = dayjs.utc(dateString)
                setBulkFinalizeEndDate(utcDate)
              } else {
                setBulkFinalizeEndDate(null)
              }
            }}
            format={DATE_FORMAT}
            shouldDisableDate={(date) => shouldDisableDateForPeriod(date, selectedPeriodId)}
            slotProps={{
              textField: {
                variant: 'outlined',
                fullWidth: true,
                InputProps: {
                  readOnly: true,
                },
                InputLabelProps: { shrink: true },
                sx: {
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '21px',
                    '& fieldset': {
                      borderRadius: '21px',
                    },
                  },
                },
                helperText: 'Click calendar icon to select date',
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setFinalizeDialogOpen(false)
              setBulkFinalizeEndDate(null)
            }}
            disabled={isBulkClosingPlans}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmFinalize}
            variant="contained"
            color="primary"
            disabled={!bulkFinalizeEndDate || isBulkClosingPlans}
            startIcon={isBulkClosingPlans ? <CircularProgress size={20} /> : null}
          >
            Finalize Plans
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Rollover Dialog */}
      {rolloverDialogOpen && (
        <RolloverPlanDialog
          open={rolloverDialogOpen}
          onClose={() => {
            setRolloverDialogOpen(false)
            // Refresh data when closing modal to ensure we have latest state
            refetchPlans()
            refetchTemplates()
          }}
          onRollover={handleConfirmRollover}
          periods={periodsArray}
          isLoading={false}
          theme={theme}
          fromPeriodLabel={periodsArray.find((p: Period) => p.id === selectedPeriodId)?.label || 'Current Period'}
          fromPeriodId={selectedPeriodId}
          plans={getSelectedPlansForRollover()}
          periodIdLabelMap={createMergedIdLabelMap(periodsArray)}
          showSelectPeriodStep={true}
        />
      )}

      {/* Single Plan Close Modal */}
      <Dialog open={singleFinalizeModalOpen} onClose={handleCloseFinalizeModal}>
        <DialogTitle>Close Plan</DialogTitle>
        <DialogContent>
          {selectedPlanToFinalize && (
            <Typography sx={{ mb: 2 }}>
              Closing plan for{' '}
              <strong>
                {selectedPlanToFinalize.employeeFirstName} {selectedPlanToFinalize.employeeLastName}
              </strong>
            </Typography>
          )}
          <Typography sx={{ mb: 1 }}>
            To close this plan, please select an <strong>End Date</strong> below.
          </Typography>
          {selectedPlanToFinalize?.periodId && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              End date must be within the period range:{' '}
              <strong>{getPeriodDateRangeText(selectedPlanToFinalize.periodId)}</strong>
            </Typography>
          )}
          <DesktopDatePicker
            label="End Date"
            value={finalizeEndDate}
            onChange={(newValue: Dayjs | null) => {
              if (newValue) {
                const dateString = newValue.format('YYYY-MM-DD')
                const utcDate = dayjs.utc(dateString)
                setFinalizeEndDate(utcDate)
              } else {
                setFinalizeEndDate(null)
              }
            }}
            format={DATE_FORMAT}
            shouldDisableDate={(date) => shouldDisableDateForPeriod(date, selectedPlanToFinalize?.periodId || '')}
            slotProps={{
              textField: {
                variant: 'outlined',
                fullWidth: true,
                InputProps: {
                  readOnly: true,
                },
                InputLabelProps: { shrink: true },
                sx: {
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '21px',
                    '& fieldset': {
                      borderRadius: '21px',
                    },
                  },
                },
                helperText: 'Click calendar icon to select date',
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFinalizeModal} disabled={isClosingPlan}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmFinalizePlan}
            color="primary"
            variant="contained"
            disabled={!finalizeEndDate || isClosingPlan}
            startIcon={isClosingPlan ? <CircularProgress size={20} /> : null}
          >
            Close Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Single Plan Rollover Modal */}
      {selectedPlanToRollover && (
        <RolloverPlanDialog
          open={rolloverModalOpen}
          onClose={handleCloseRolloverModal}
          onRollover={handleConfirmSingleRollover}
          periods={rolloverPeriods?.periods || []}
          isLoading={isLoadingRolloverPeriods}
          theme={theme}
          fromPeriodLabel={
            periodsArray.find((p: Period) => p.id === selectedPlanToRollover.periodId)?.label || 'Current Period'
          }
          fromPeriodId={selectedPlanToRollover.periodId}
          plans={[
            {
              id: selectedPlanToRollover.id,
              revision: selectedPlanToRollover.revision,
              userId: selectedPlanToRollover.userId,
              templateId: selectedPlanToRollover.templateId || '',
              employeeFirstName: selectedPlanToRollover.employeeFirstName,
              employeeLastName: selectedPlanToRollover.employeeLastName,
              baseSalary: selectedPlanToRollover.baseSalary,
              position: selectedPlanToRollover.position,
              periodProration: selectedPlanToRollover.periodProration,
              compensationModel: selectedPlanToRollover.compensationModel,
              variablePercent: selectedPlanToRollover.variablePercent,
              variableCap: selectedPlanToRollover.variableCap,
            },
          ]}
          periodIdLabelMap={rolloverPeriodIdLabelMap}
          showSelectPeriodStep={true}
        />
      )}

      {/* Archive Plan Modal */}
      <Dialog open={archiveModalOpen} onClose={handleCloseArchiveModal}>
        <DialogTitle>Archive Plan</DialogTitle>
        <DialogContent>
          {selectedPlanToArchive && (
            <Typography sx={{ mb: 2 }}>
              Are you sure you want to archive the plan for{' '}
              <strong>
                {selectedPlanToArchive.employeeFirstName} {selectedPlanToArchive.employeeLastName}
              </strong>
              ?
            </Typography>
          )}
          <Typography>This action will move the plan to the archived state. This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseArchiveModal} disabled={isArchivingPlan}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmArchivePlan}
            color="primary"
            variant="contained"
            disabled={isArchivingPlan}
            startIcon={isArchivingPlan ? <CircularProgress size={20} /> : null}
          >
            Archive Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reopen Plan Modal */}
      <Dialog open={reopenModalOpen} onClose={handleCloseReopenModal}>
        <DialogTitle>Reopen Plan</DialogTitle>
        <DialogContent>
          {selectedPlanToReopen && (
            <Typography sx={{ mb: 2 }}>
              Are you sure you want to reopen the plan for{' '}
              <strong>
                {selectedPlanToReopen.employeeFirstName} {selectedPlanToReopen.employeeLastName}
              </strong>
              ?
            </Typography>
          )}
          <Typography>This action will remove the end date and make the plan active again.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReopenModal} disabled={isReopeningPlan}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmReopenPlan}
            color="primary"
            variant="contained"
            disabled={isReopeningPlan}
            startIcon={isReopeningPlan ? <CircularProgress size={20} /> : null}
          >
            Reopen Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Finalization Modal */}
      <Dialog open={clearFinalizationModalOpen} onClose={handleCloseClearFinalizationModal}>
        <DialogTitle>Clear Finalization</DialogTitle>
        <DialogContent>
          {selectedPlanToClearFinalization && (
            <Typography sx={{ mb: 2 }}>
              Are you sure you want to clear the finalization for{' '}
              <strong>
                {selectedPlanToClearFinalization.employeeFirstName} {selectedPlanToClearFinalization.employeeLastName}
              </strong>
              ?
            </Typography>
          )}
          <Typography>
            This will remove the finalization timestamp. The plan's end date will remain unchanged.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseClearFinalizationModal} disabled={isClearingFinalization}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmClearFinalization}
            color="primary"
            variant="contained"
            disabled={isClearingFinalization}
            startIcon={isClearingFinalization ? <CircularProgress size={20} /> : null}
          >
            Clear Finalization
          </Button>
        </DialogActions>
      </Dialog>

      {/* Plan Lifecycle Info Dialog */}
      <Dialog open={lifecycleInfoOpen} onClose={() => setLifecycleInfoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Plan Lifecycle</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
            1. Open
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            The plan is active with no end date. Actuals can be submitted against it.
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            2. Closed
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            An end date has been set. The plan will be finalized automatically once all actuals are submitted for the
            period.
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            3. Finalized
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            All actuals have been submitted and the plan is complete. No further changes to actuals are expected.
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Actions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Close Plan</strong> — Sets an end date on the plan.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Reopen Plan</strong> — Removes the end date, making the plan active again. Requires clearing
            finalization first if the plan is finalized.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Clear Finalization</strong> — Removes only the finalization timestamp. The end date is kept so the
            plan can be re-finalized once actuals are corrected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLifecycleInfoOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Update Compensation Dialog */}
      {updateCompensationDialogOpen && (
        <UpdateCompensationDialog
          open={updateCompensationDialogOpen}
          onClose={handleCloseUpdateCompensationDialog}
          onUpdate={handleConfirmUpdateCompensation}
          plans={getSelectedPlansForUpdate()}
          periods={periodsArray}
        />
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Bulk Action Bar */}
      <ButtonActionBar show={selectedPlans.size > 0} maxWidth="900px" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={2}>
          <Box sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
            {selectedPlans.size} {selectedPlans.size === 1 ? 'plan' : 'plans'} selected
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<CloseCircleOutlined />}
            onClick={handleCancelSelection}
          >
            Cancel
          </Button>
          <Tooltip
            title={
              selectedPlans.size === 0
                ? 'Select plans to update compensation'
                : 'Update compensation for selected plans'
            }
          >
            <span>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleUpdateCompensation}
                disabled={selectedPlans.size === 0}
              >
                Update Compensation
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              hasSelectedFinalizedPlans()
                ? 'Cannot finalize - some selected plans are already finalized'
                : selectedPlans.size === 0
                  ? 'Select plans to finalize'
                  : 'Finalize selected plans'
            }
          >
            <span>
              <Button
                variant="contained"
                color="primary"
                onClick={handleFinalizePlans}
                disabled={selectedPlans.size === 0 || hasSelectedFinalizedPlans()}
              >
                Finalize Selected
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              hasSelectedMixedTemplates()
                ? 'You can only rollover one template at a time'
                : selectedPlans.size === 0
                  ? 'Select plans to rollover'
                  : 'Rollover selected plans'
            }
          >
            <span>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleRolloverPlans}
                disabled={selectedPlans.size === 0 || hasSelectedMixedTemplates()}
              >
                Rollover Selected
              </Button>
            </span>
          </Tooltip>
          {canBulkHideOrShow() && (
            <Tooltip
              title={
                selectedPlans.size === 0
                  ? shouldShowHideButton()
                    ? 'Select plans to hide'
                    : 'Select plans to make visible'
                  : shouldShowHideButton()
                    ? 'Hide selected plans'
                    : 'Make selected plans visible'
              }
            >
              <span>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleBulkHideOrShowPlans}
                  disabled={selectedPlans.size === 0 || isHidingPlan || isShowingPlan}
                  startIcon={isHidingPlan || isShowingPlan ? <CircularProgress size={20} /> : null}
                >
                  {shouldShowHideButton() ? 'Make Hidden' : 'Make Visible'}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </ButtonActionBar>

      {/* Edit User Dialog */}
      <EditUserDialog
        open={editUserDialogOpen}
        onClose={handleCloseEditUserDialog}
        userId={selectedUserIdForEdit}
        selectedOrganizationId={selectedOrgIdForEdit}
      />
    </>
  )
}

const LoadingSpinner: FC = () => (
  <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
    <CircularProgress />
  </Box>
)

interface TemplateSelectorProps {
  templateOptions: { id: string; label: string }[]
  selectedTemplate: string
  onTemplateChange: (templateId: string) => void
}

const TemplateSelector: FC<TemplateSelectorProps> = ({ templateOptions, selectedTemplate, onTemplateChange }) => {
  const theme = useTheme()

  const handleTemplateChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      onTemplateChange(event.target.value)
    },
    [onTemplateChange]
  )

  return (
    <FormControl variant="outlined" sx={{ width: '250px' }}>
      <InputLabel
        shrink
        variant="outlined"
        sx={{
          '&.MuiInputLabel-shrink': {
            background: theme.palette.background.default,
          },
        }}
      >
        Template
      </InputLabel>
      <Select
        labelId="template-select-label"
        id="template-select"
        variant="outlined"
        value={selectedTemplate}
        label="Template"
        style={{ borderRadius: '21px' }}
        onChange={handleTemplateChange}
        displayEmpty
      >
        {templateOptions.map((template) => (
          <MenuItem key={template.id} value={template.id}>
            {template.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

export default PeriodPlansManagement
